import React, { useState } from 'react';
import apiService from "../Common/Configurations/APIService";
import config from '../Common/Configurations/APIConfig';
import { useAuth } from '../Common/Configurations/AuthContext'; 
import DefaultData from '../CompanyData/DefaultData';

const initialValues = {
    name: '',
    email: '',
    password: '',
    phoneNumber: '',
};

export default function RegisterModal({ show, onClose, onSwitchToLogin, onLoginSuccess }) {
    const { login } = useAuth(); 
    const [values, setValues] = useState(initialValues);
    const [errors, setErrors] = useState({}); 
    const [apiError, setApiError] = useState(''); 
    const [success, setSuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setValues({ ...values, [name]: value });

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: undefined }));
        }
    };


    const validate = () => {
        let tempErrors = {};
        if (!values.name.trim()) {
            tempErrors.name = "Full name is required.";
        }
        if (!values.email.trim()) {
            tempErrors.email = "Email is required.";
        } else if (!/\S+@\S+\.\S+/.test(values.email)) { 
            tempErrors.email = "Please enter a valid email address.";
        }
        if (!values.phoneNumber.trim()) {
            tempErrors.phoneNumber = "Phone number is required.";
        }
        if (!values.password) {
            tempErrors.password = "Password is required.";
        } else if (values.password.length < 6) { 
            tempErrors.password = "Password must be at least 6 characters.";
        }

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setApiError('');
        setSuccess('');

        if (validate()) {
            setIsLoading(true);
            const registrationData = {
                ...values,
                username: values.email,
                roleName: 'CUSTOMER',
                branchId: DefaultData.DEFAULTBRANCHID
            };

            try {
                await apiService.post(config.CREATEUSER, registrationData);
                
                setSuccess('Registration successful! Logging you in...');
                const loginData = {
                    username: values.email,
                    password: values.password,
                    companyId: DefaultData.DEFAULTCOMPANYID,
                };
                const loginResult = await apiService.post(config.LOGIN, loginData);

                if (loginResult.accessToken) {
                    login(loginResult.accessToken); 
                    setTimeout(() => onLoginSuccess(), 1500);
                } else {
                    setApiError('Auto-login failed. Please try logging in manually.');
                    setTimeout(() => onSwitchToLogin(), 2000);
                }
            } catch (err) {
                setApiError(err.response?.data?.message || 'Registration failed. Please try again.');
                setIsLoading(false); 
            }
        }
    };

    const applyErrorClass = (field) => (errors[field] ? 'is-invalid' : '');

    if (!show) {
        return null;
    }

    return (
        <>
            <div className="modal-backdrop fade show"></div>
            <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Create an Account</h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body">
                            <p className="text-muted text-center">Get started with your free account.</p>
                            {apiError && <div className="alert alert-danger">{apiError}</div>}
                            {success && <div className="alert alert-success">{success}</div>}
                            <form onSubmit={handleSubmit} noValidate>
                                {/* --- 3. Updated form inputs with proper error display --- */}
                                <div className="mb-3">
                                    <label className="form-label">Full Name</label>
                                    <input className={`form-control ${applyErrorClass('name')}`} name="name" type="text" value={values.name} onChange={handleInputChange} placeholder="Enter your full name" />
                                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Email Address</label>
                                    <input className={`form-control ${applyErrorClass('email')}`} name="email" type="email" value={values.email} onChange={handleInputChange} placeholder="Enter your email" />
                                    {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Phone Number</label>
                                    <input className={`form-control ${applyErrorClass('phoneNumber')}`} name="phoneNumber" type="tel" value={values.phoneNumber} onChange={handleInputChange} placeholder="Enter your phone number" />
                                    {errors.phoneNumber && <div className="invalid-feedback">{errors.phoneNumber}</div>}
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Password</label>
                                    <div className="position-relative">
                                        <input
                                            className={`form-control ${applyErrorClass('password')}`}
                                            name="password"
                                            type={showPassword ? 'text' : 'password'}
                                            value={values.password}
                                            onChange={handleInputChange}
                                            placeholder="Create a password"
                                        />
                                        <span className="password-toggle-icon" onClick={() => setShowPassword(!showPassword)}>
                                            <i className={showPassword ? "ri-eye-fill" : "ri-eye-off-fill"}></i>
                                        </span>
                                    </div>
                                    {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                                </div>
                                <div className="mt-4">
                                    <button type="submit" className="btn btn-success w-100" disabled={isLoading}>
                                        {isLoading ? 'Processing...' : 'Register & Login'}
                                    </button>
                                </div>
                            </form>
                        </div>
                        <div className="modal-footer justify-content-center">
                            <p className="mb-0">
                                Already have an account?{' '}
                                <button type="button" className="btn btn-link p-0 fw-semibold text-decoration-underline" onClick={onSwitchToLogin}>
                                    Sign In
                                </button>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}